import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';

const SOCIAL_LINKS = [
  { label: 'Facebook',  href: '#', icon: (p: string) => <svg className={p} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
  { label: 'Instagram', href: '#', icon: (p: string) => <svg className={p} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
  { label: 'Twitter',   href: '#', icon: (p: string) => <svg className={p} fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  { label: 'LinkedIn',  href: '#', icon: (p: string) => <svg className={p} fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  { label: 'YouTube',   href: '#', icon: (p: string) => <svg className={p} fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
];

// Helper to render social icon
const SocialIcon = ({ icon: Icon, className }: { icon: (p: string) => React.ReactNode; className: string }) => <>{Icon(className)}</>;

const Footer: React.FC = () => {
  const { getSetting } = useSettings();
  const siteName = getSetting('site_name', 'ToleMate');
  const contactEmail = getSetting('contact_email', 'info@tolemate.com');
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-400 mt-auto">
      {/* Main grid */}
      <div className="container-custom py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand column — spans 2 on large screens */}
          <div className="lg:col-span-2 text-center sm:text-left">
            <Link to="/" className="inline-flex items-center gap-2.5 mb-4">
              <img src="/apple-touch-icon.png" alt={siteName} className="w-9 h-9 rounded-xl shadow-lg" />
              <span className="font-bold text-xl text-white">{siteName}</span>
            </Link>
            <p className="text-sm leading-relaxed text-gray-500 mb-5 max-w-xs mx-auto sm:mx-0">
              Connecting you with trusted local professionals for every home and business need. Your satisfaction is our priority.
            </p>

            {/* Social icons */}
            <div className="flex gap-2 justify-center sm:justify-start">
              {SOCIAL_LINKS.map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 bg-gray-800 hover:bg-primary-600 rounded-lg flex items-center justify-center text-base transition-colors"
                >
                  <SocialIcon icon={s.icon} className="w-4 h-4" />
                </a>
              ))}
            </div>

            <p className="text-xs text-gray-600 mt-5">{contactEmail}</p>
          </div>

          {/* Services */}
          <div className="text-center sm:text-left">
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Services</h3>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'Home Repair',    to: '/services?search=Home+Repair' },
                { label: 'Plumbing',       to: '/services?search=Plumbing' },
                { label: 'Electrical',     to: '/services?search=Electrical' },
                { label: 'Cleaning',       to: '/services?search=Cleaning' },
                { label: 'Tech Support',   to: '/services?search=Tech+Support' },
                { label: 'All Services',   to: '/services' },
              ].map(item => (
                <li key={item.label}>
                  <Link to={item.to} className="hover:text-white transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="text-center sm:text-left">
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Company</h3>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'About Us',          to: '/about' },
                { label: 'Contact',           to: '/contact' },
                { label: 'Terms & Conditions',to: '/terms' },
                { label: 'Privacy Policy',    to: '/privacy' },
              ].map(item => (
                <li key={item.label}>
                  <Link to={item.to} className="hover:text-white transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Professionals */}
          <div className="text-center sm:text-left">
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">For Professionals</h3>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'Become a Provider', to: '/register?role=vendor' },
                { label: 'Provider Login',    to: '/login' },
                { label: 'Marketplace',       to: '/marketplace' },
                { label: 'Post a Job',        to: '/post-request' },
              ].map(item => (
                <li key={item.label}>
                  <Link to={item.to} className="hover:text-white transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>

            {/* App download mini */}
            <div className="mt-6 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">Download App</p>
              {[{ store: 'App Store', icon: '/app-store-badge.png' }, { store: 'Google Play', icon: '/google-play-badge.png' }].map(a => (
                <span key={a.store} className="inline-block opacity-60 cursor-not-allowed" title="Coming soon">
                  <img src={a.icon} alt={a.store} loading="lazy" className="h-[42px] w-auto" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-gray-600">
          <p>© {currentYear} {siteName}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span>All systems operational</span>
            </div>
            <Link to="/terms" className="hover:text-gray-400 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">Privacy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
